/* ==========================================================================
   Bedrock — utilitaires partagés
   Vanilla, sans dépendance. Tout est exposé sous window.Bedrock.
   ========================================================================== */

(function () {
  'use strict';

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     Révélation au scroll — appliqué à tout .bdr-reveal présent ou ajouté
     ------------------------------------------------------------------------ */

  let revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;

    revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );

    return revealObserver;
  }

  function observeReveals(root) {
    const scope = root || document;
    if (document.body?.dataset.bdrReveal === 'true') {
      scope.querySelectorAll([
        '.bdr__header',
        '.bdr-brand-directory__card',
        '.bdr-content-hub__card',
        '.bdr-icon-columns__item',
        '.bdr-jobs-board__card',
        '.bdr-logo-list__item',
        '.bdr-media-grid__tile',
        '.bdr-press__card',
        '.bdr-pricing-table__card',
        '.bdr-profile-cards__card',
        '.bdr-profile-router__card',
        '.bdr-shop-the-look__card',
        '.bdr-stat-bar__item',
        '.bdr-testimonials__card',
        '.bdr-trust-badges__item',
        '.bdr-ugc-card',
      ].join(',')).forEach((element) => {
        if (!element.hasAttribute('data-bdr-no-reveal')) element.classList.add('bdr-reveal');
      });
    }
    const observer = getRevealObserver();
    const targets = scope.querySelectorAll('.bdr-reveal:not(.is-inview)');

    if (!observer || reduceMotion()) {
      targets.forEach((el) => el.classList.add('is-inview'));
      return;
    }

    targets.forEach((el, index) => {
      if (!el.style.getPropertyValue('--bdr-reveal-delay')) {
        el.style.setProperty('--bdr-reveal-delay', `${Math.min(index, 8) * 60}ms`);
      }
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------------
     Progression de lecture et parallax léger. Un seul cycle rAF est partagé
     entre tous les éléments afin de limiter les recalculs au scroll.
     ------------------------------------------------------------------------ */

  const parallaxItems = new Set();
  let motionFrame = null;

  function motionDisabled() {
    return reduceMotion() || document.body?.dataset.bdrMotion === 'none';
  }

  function observeParallax(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-bdr-parallax]').forEach((element) => parallaxItems.add(element));
    scheduleMotionFrame();
  }

  function updateMotionEffects() {
    motionFrame = null;
    const progress = document.querySelector('[data-bdr-scroll-progress]');
    if (progress) {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const value = scrollable > 0 ? Math.min(1, Math.max(0, window.scrollY / scrollable)) : 0;
      progress.style.setProperty('--bdr-scroll-progress', value.toFixed(4));
    }

    parallaxItems.forEach((element) => {
      if (!element.isConnected) {
        parallaxItems.delete(element);
        return;
      }
      if (motionDisabled()) {
        element.style.removeProperty('--bdr-parallax-y');
        return;
      }
      const rect = element.getBoundingClientRect();
      if (rect.bottom < -100 || rect.top > window.innerHeight + 100) return;
      const strength = Math.min(48, Math.max(0, Number(element.dataset.bdrParallaxStrength) || 18));
      const centerOffset = (window.innerHeight / 2 - (rect.top + rect.height / 2)) / window.innerHeight;
      const offset = Math.max(-strength, Math.min(strength, centerOffset * strength * 2));
      element.style.setProperty('--bdr-parallax-y', `${offset.toFixed(2)}px`);
    });
  }

  function scheduleMotionFrame() {
    if (motionFrame) return;
    motionFrame = window.requestAnimationFrame(updateMotionEffects);
  }

  window.addEventListener('scroll', scheduleMotionFrame, { passive: true });
  window.addEventListener('resize', scheduleMotionFrame, { passive: true });

  /* ------------------------------------------------------------------------
     Vidéos en fond, lues uniquement quand visibles — jamais de lecture
     automatique si l'utilisateur préfère un mouvement réduit (le poster
     reste alors affiché en statique). Structure attendue :
       <video data-bdr-autoplay-inview data-src="…" muted loop playsinline
              preload="none" poster="…"></video>
     ------------------------------------------------------------------------ */

  let inviewVideoObserver = null;

  function getInviewVideoObserver() {
    if (inviewVideoObserver || !('IntersectionObserver' in window)) return inviewVideoObserver;

    inviewVideoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (video.dataset.src && !video.src) video.src = video.dataset.src;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    return inviewVideoObserver;
  }

  function observeInviewVideos(root) {
    if (reduceMotion()) return; // le poster reste affiché, jamais de lecture auto

    const scope = root || document;
    const targets = scope.querySelectorAll('video[data-bdr-autoplay-inview]');
    const observer = getInviewVideoObserver();
    if (!observer || !targets.length) return;

    targets.forEach((video) => observer.observe(video));
  }

  /* ------------------------------------------------------------------------
     <bdr-carousel> — carrousel scroll-snap accessible
     Structure attendue :
       <bdr-carousel>
         <div class="bdr-scroller" data-bdr-track> …slides… </div>
         <button data-bdr-prev>…</button>
         <button data-bdr-next>…</button>
         <ol data-bdr-dots></ol>            (optionnel)
       </bdr-carousel>
     ------------------------------------------------------------------------ */

  class BdrCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-bdr-track]');
      if (!this.track) return;

      this.prevBtn = this.querySelector('[data-bdr-prev]');
      this.nextBtn = this.querySelector('[data-bdr-next]');
      this.dotsHost = this.querySelector('[data-bdr-dots]');

      this.onScroll = this.throttle(this.update.bind(this), 100);
      this.onPrev = () => this.scrollByPage(-1);
      this.onNext = () => this.scrollByPage(1);
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          this.scrollByPage(-1);
        } else if (event.key === 'ArrowRight') {
          this.scrollByPage(1);
        }
      };

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
      this.addEventListener('keydown', this.onKeydown);

      if (this.dotsHost) this.buildDots();

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      this.update();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get slides() {
      return Array.from(this.track.children);
    }

    scrollByPage(direction) {
      const slide = this.slides[0];
      if (!slide) return;
      const step = slide.getBoundingClientRect().width + this.gap();
      this.track.scrollBy({ left: step * direction, behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    gap() {
      return parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    }

    buildDots() {
      this.dotsHost.innerHTML = '';
      this.slides.forEach((slide, index) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bdr-dots__btn';
        btn.setAttribute('aria-label', `${index + 1}`);
        btn.addEventListener('click', () => {
          this.track.scrollTo({
            left: slide.offsetLeft - this.track.offsetLeft,
            behavior: reduceMotion() ? 'auto' : 'smooth',
          });
        });
        li.appendChild(btn);
        this.dotsHost.appendChild(li);
      });
    }

    update() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      const overflows = scrollWidth > clientWidth + 2;

      this.toggleAttribute('data-bdr-static', !overflows);

      const focused = document.activeElement;
      if (this.prevBtn) {
        if (atStart && focused === this.prevBtn && this.nextBtn && !atEnd) this.nextBtn.focus();
        this.prevBtn.disabled = atStart;
      }
      if (this.nextBtn) {
        if (atEnd && focused === this.nextBtn && this.prevBtn && !atStart) this.prevBtn.focus();
        this.nextBtn.disabled = atEnd;
      }

      if (this.dotsHost) {
        let activeIndex = 0;
        if (atEnd) {
          activeIndex = this.slides.length - 1;
        } else {
          let closest = Infinity;
          this.slides.forEach((slide, index) => {
            const start = slide.offsetLeft - this.track.offsetLeft;
            const distance = Math.abs(start - scrollLeft);
            if (distance < closest) {
              closest = distance;
              activeIndex = index;
            }
          });
        }
        Array.from(this.dotsHost.querySelectorAll('.bdr-dots__btn')).forEach((btn, index) => {
          btn.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
        });
      }
    }

    throttle(fn, wait) {
      let last = 0;
      let timer = null;
      return function throttled() {
        const now = Date.now();
        const remaining = wait - (now - last);
        if (remaining <= 0) {
          last = now;
          fn();
        } else if (!timer) {
          timer = setTimeout(() => {
            last = Date.now();
            timer = null;
            fn();
          }, remaining);
        }
      };
    }
  }

  /* ------------------------------------------------------------------------
     <bdr-dialog-trigger> — ouvre le <dialog> ciblé par data-bdr-target
     ------------------------------------------------------------------------ */

  class BdrDialogTrigger extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button') || this;
      this.onClick = this.open.bind(this);
      this.button.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    open() {
      const id = this.dataset.bdrTarget;
      const dialog = id && document.getElementById(id);
      if (!dialog || typeof dialog.showModal !== 'function') return;
      dialog.showModal();
      const close = dialog.querySelector('[data-bdr-close]');
      if (close) close.addEventListener('click', () => dialog.close(), { once: true });
      dialog.addEventListener(
        'click',
        (event) => {
          if (event.target === dialog) dialog.close();
        },
        { once: true }
      );
    }
  }

  /* ------------------------------------------------------------------------
     Helpers exposés
     ------------------------------------------------------------------------ */

  const Bedrock = {
    reduceMotion,
    observeReveals,
    observeInviewVideos,
    observeParallax,

    /** Formate un montant exprimé dans l'unité monétaire mineure de la boutique. */
    money(minorUnits) {
      const currency = window.Shopify?.currency?.active || '';
      const locale = document.documentElement.lang || undefined;
      const numericValue = Number(minorUnits);

      if (!Number.isFinite(numericValue)) return '';

      if (currency) {
        try {
          const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
          const decimals = formatter.resolvedOptions().maximumFractionDigits;
          return formatter.format(numericValue / 10 ** decimals);
        } catch (error) {
          /* Le code devise peut être absent ou invalide dans certains aperçus éditeur. */
        }
      }

      return new Intl.NumberFormat(locale).format(numericValue / 100);
    },

    /** Récupère l'état du panier. */
    async fetchCart() {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('cart fetch failed');
      return response.json();
    },

    /** Rendu d'une section côté serveur (Section Rendering API). */
    async fetchSection(sectionId, url) {
      const base = url || window.location.pathname;
      const separator = base.includes('?') ? '&' : '?';
      const response = await fetch(`${base}${separator}section_id=${sectionId}`);
      if (!response.ok) throw new Error('section fetch failed');
      return response.text();
    },

    /** Ajoute un ou plusieurs articles au panier via Cart API. */
    async addToCart(items) {
      const payload = Array.isArray(items) ? { items } : items;
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('cart add failed');
      const data = await response.json();
      document.dispatchEvent(new CustomEvent('bdr:cart:updated', { detail: data, bubbles: true }));
      return data;
    },

    /** Modifie la quantité d'une ligne ou réinitialise le panier via Cart API. */
    async changeCart(payload) {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('cart change failed');
      const data = await response.json();
      document.dispatchEvent(new CustomEvent('bdr:cart:updated', { detail: data, bubbles: true }));
      return data;
    },

    /** Piège le focus dans un conteneur tant qu'il est ouvert. */
    trapFocus(container) {
      const selector =
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
      const handler = (event) => {
        if (event.key !== 'Tab') return;
        const items = Array.from(container.querySelectorAll(selector)).filter(
          (el) => el.offsetParent !== null
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      };
      container.addEventListener('keydown', handler);
      return () => container.removeEventListener('keydown', handler);
    },
  };

  /* ------------------------------------------------------------------------
     Enregistrement
     ------------------------------------------------------------------------ */

  if (!customElements.get('bdr-carousel')) customElements.define('bdr-carousel', BdrCarousel);
  if (!customElements.get('bdr-dialog-trigger')) customElements.define('bdr-dialog-trigger', BdrDialogTrigger);

  window.Bedrock = Bedrock;

  const boot = () => {
    observeReveals(document);
    observeInviewVideos(document);
    observeParallax(document);
    scheduleMotionFrame();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    observeReveals(event.target);
    observeInviewVideos(event.target);
    observeParallax(event.target);
  });
})();
